import * as React from 'react';
import {
  Box, Typography, Button, TextField, Dialog, IconButton, InputAdornment, 
  Avatar, DialogContent, DialogTitle, DialogActions
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';

import { GET_QUERIES, SET_MODE, RESOLVE_QUERY, UPDATE_QUERY } from '../../api/query';
import { GET_PRESIGNED_URL } from '../../api/presignedurl';
import { BASE_URL } from '../../api/client';

import ReplyIcon from '@mui/icons-material/Reply';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

import './Query.css';

const SENDERS = { ADMIN: 'Admin', USER: 'User' };
const REPLY_MODES = { EMAIL: 'email', chat: 'chat', PENDING: 'pending' };
const TICKET_STATUS = { CLOSED: 'Closed' };

const Query = () => {
  const [queries, setQueries] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [choiceOpen, setChoiceOpen] = React.useState(false);
  const [selectedQuery, setSelectedQuery] = React.useState(null);
  const [replyText, setReplyText] = React.useState("");
  const [imageFile, setImageFile] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const chatEndRef = React.useRef(null);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  React.useEffect(() => {
    if (open) scrollToBottom();
  }, [selectedQuery?.messages, open]);

  const fetchQueries = async () => {
    try {
      const res = await axios.get(GET_QUERIES);
      const formatted = res.data.data.map(item => ({ ...item, id: item._id }));
      setQueries(formatted);
      if (open && selectedQuery) {
        const updated = formatted.find(q => q._id === selectedQuery._id);
        if (updated) setSelectedQuery(updated);
      }
    } catch (err) { console.error("Fetch Error:", err); }
  };

  React.useEffect(() => { fetchQueries(); }, []);

  const handleOpenReply = (query) => {
    setSelectedQuery(query);
    if (!query.replyMode || query.replyMode === REPLY_MODES.PENDING) {
      setChoiceOpen(true);
    } else { setOpen(true); }
  };

  const handleSetMode = async (mode) => {
    try {
      await axios.patch(`${SET_MODE}/${selectedQuery._id}`, { replyMode: mode });
      setChoiceOpen(false);
      fetchQueries();
      if (mode === REPLY_MODES.chat) setOpen(true);
    } catch (error) { console.error(error); }
  };

  const handleResolve = async (id) => {
    try {
      await axios.patch(`${RESOLVE_QUERY}/${id}`);
      fetchQueries();
    } catch (error) { console.error(error); }
  };

  const handleClose = () => {
    setOpen(false);
    setReplyText("");
    setImageFile(null);
    setSelectedQuery(null);
  };

  const handleSendReply = async () => {
    if (!replyText.trim() && !imageFile) return;
    setLoading(true);

    try {
      let finalImageUrl = null;

      if (imageFile) {
        const filePath = `chat_attachments/${Date.now()}-${imageFile.name}`;
        const presignedRes = await axios.post(GET_PRESIGNED_URL, {
          path: filePath,
          contentType: imageFile.type,
        });

        const { uploadUrl, publicUrl } = presignedRes.data;

        await axios.put(uploadUrl, imageFile, {
          headers: { "Content-Type": imageFile.type }
        });

        finalImageUrl = publicUrl;
      }

      const messagePayload = {
        sender: SENDERS.ADMIN,
        message: replyText,
        image: finalImageUrl 
      };

      await axios.put(`${UPDATE_QUERY}/${selectedQuery._id}`, messagePayload);
      
      setReplyText("");
      setImageFile(null);
      fetchQueries();
    } catch (error) { 
      console.error("Chat Update Failed:", error); 
      alert("Failed to send message. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { field: 'name', headerName: 'Name', width: 150 },
    { field: 'ticketNumber', headerName: 'Ticket #', width: 120 },
    {
      field: 'latestMessage',
      headerName: 'Latest Message',
      flex: 1,
      renderCell: (params) => {
        if (params.row.replyMode === REPLY_MODES.EMAIL) return <Typography className="email-status-text">Replied via Email</Typography>;
        const messages = params.row.messages || [];
        if (messages.length === 0) return "No messages";
        const lastMsg = messages[messages.length - 1];
        return (
          <Typography variant="body2" color="textSecondary" noWrap>
            {lastMsg.sender === SENDERS.ADMIN && <strong>Replied: </strong>} 
            {lastMsg.image ? "📷 Attachment" : lastMsg.message}
          </Typography>
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Response',
      width: 140,
      renderCell: (params) => (
        <Button
          variant="contained" size="small" startIcon={<ReplyIcon />}
          onClick={() => handleOpenReply(params.row)}
          disabled={params.row.status === TICKET_STATUS.CLOSED || params.row.replyMode === REPLY_MODES.EMAIL}
          sx={{ bgcolor: '#2196f3', textTransform: 'none', mt: 1 }}
        >Revert</Button>
      ),
    },
    {
      field: 'statusAction',
      headerName: 'Close Status',
      width: 130,
      renderCell: (params) => (
        <Button
          className={params.row.status === TICKET_STATUS.CLOSED ? "btn-status-closed" : "btn-status-open"}
          variant="contained" size="small" startIcon={<CheckCircleIcon />}
          onClick={() => handleResolve(params.row._id)}
          disabled={params.row.status === TICKET_STATUS.CLOSED || params.row.replyMode === REPLY_MODES.EMAIL}
          sx={{ mt: 1 }}
        >
          {params.row.status === TICKET_STATUS.CLOSED ? 'Closed' : 'Close'}
        </Button>
      ),
    },
  ];

  return (
    <Box className="query-container">
      <Typography variant='h5' className="query-title">Support Dashboard</Typography>
      
      <Box className="datagrid-wrapper">
        <DataGrid rows={queries} columns={columns} disableRowSelectionOnClick rowHeight={65} />
      </Box>

      <Dialog open={choiceOpen} onClose={() => setChoiceOpen(false)}>
        <DialogTitle className="dialog-header">Select Method</DialogTitle>
        <DialogContent className="dialog-body-choice">
          <Typography>Initialize response for {selectedQuery?.name}?</Typography>
        </DialogContent>
        <DialogActions className="dialog-footer-choice">
          <Button variant="outlined" onClick={() => handleSetMode(REPLY_MODES.EMAIL)}>Email</Button>
          <Button variant="contained" onClick={() => handleSetMode(REPLY_MODES.chat)}>Live Chat</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '24px', height: '85vh' } }}>
        <DialogTitle className="chat-header-pill">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: '#6c5ce7' }}><SupportAgentIcon /></Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight="700" lineHeight={1.2}>{selectedQuery?.name}</Typography>
              <Typography variant="caption" color="textSecondary">Ticket #{selectedQuery?.ticketNumber}</Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
        </DialogTitle>

        <DialogContent className="chat-body-pill">
          {selectedQuery?.messages?.map((msg, index) => {
            const isAdmin = msg.sender === SENDERS.ADMIN
            const imageSrc = msg.image?.startsWith('http') ? msg.image : `${BASE_URL}${msg.image}`;

            return (
              <Box key={index} className={`msg-row ${isAdmin ? 'admin-row' : 'user-row'}`}>
                <Box className={`bubble ${isAdmin ? 'bubble-admin' : 'bubble-user'}`}>
                  {msg.image && (
                    <img 
                      src={imageSrc} 
                      alt="attachment" 
                      className="bubble-img"
                      onClick={() => window.open(imageSrc, '_blank')}
                    />
                  )}
                  <Typography variant="body2">{msg.message}</Typography>
                </Box>
                <Typography variant="caption" className="msg-time-pill">
                  {new Date(msg.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </Typography>
              </Box>
            );
          })}
          <div ref={chatEndRef} />
        </DialogContent>

        <Box className="chat-footer-pill">
          {imageFile && (
            <Box className="file-preview-pill">
              <Typography variant="caption" noWrap sx={{ maxWidth: '150px' }}>📎 {imageFile.name}</Typography>
              <IconButton size="small" onClick={() => setImageFile(null)}><CloseIcon sx={{ fontSize: 14 }} /></IconButton>
            </Box>
          )}
          <Box className="pill-input-container">
            <TextField 
              fullWidth 
              placeholder={loading ? "Uploading image..." : "Write a reply..."}
              multiline 
              maxRows={4} 
              value={replyText}
              disabled={loading}
              onChange={(e) => setReplyText(e.target.value)} 
              variant="standard"
              InputProps={{
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton component="label" size="small" sx={{ color: '#6c5ce7' }} disabled={loading}>
                      <AttachFileIcon />
                      <input type="file" hidden accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              className="pill-field"
            />
            <IconButton 
              className="send-btn-pill" 
              onClick={handleSendReply} 
              disabled={loading || (!replyText.trim() && !imageFile)}
            >
              <SendIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default Query;